import React from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const isWeb = Platform.OS === 'web';

/**
 * Browse pill row — matches `PublicFooter` catalog chip styling (label + arrow, pill radius).
 *
 * @param {{ route: string, label: string }[]} props.items
 * @param {(route: string) => void} props.onNavigate
 * @param {string} [props.label='Browse']
 * @param {import('react-native').ViewStyle} [props.style]
 */
export default function AppDashboardFooterBrowsePills({ items, onNavigate, label = 'Browse', style }) {
  const { colors } = useTheme();

  return (
    <View style={[styles.pillRow, { borderTopColor: colors.border_color_secondary }, style]}>
      <Text
        size="xxs"
        weight="bold"
        style={[styles.pillRowLabel, { color: colors.text_color_tertiary }]}
      >
        {label}
      </Text>
      {items.map((c) => (
        <Pressable
          key={`${c.label}:${c.route}`}
          onPress={() => onNavigate(c.route)}
          accessibilityRole="link"
          accessibilityLabel={c.label}
          style={({ pressed, hovered }) => [
            styles.pill,
            {
              borderColor: colors.border_color_secondary,
              backgroundColor: colors.solid_color_block,
            },
            isWeb && hovered && !pressed && { borderColor: colors.text_color_primary },
            pressed && { opacity: 0.85 },
            isWeb ? { cursor: 'pointer' } : undefined,
          ]}
        >
          <Text size="xs" color="primary" style={{ letterSpacing: -0.1 }}>
            {c.label}
          </Text>
          <Icon name="arrow-right" size={9} color={colors.text_color_secondary} solid />
        </Pressable>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  pillRowLabel: {
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    marginRight: 8,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    ...Platform.select({
      web: { cursor: 'pointer', transition: 'border-color 0.15s ease' },
      default: {},
    }),
  },
});
